import { CaretDown, Check } from './icons';
import { useEffect, useId, useRef, useState } from 'react';
import { cn } from './cn';

export interface FilterOption<T extends string> {
  value: T;
  label: string;
  /** Número ao lado (ex.: quantos itens com este status). */
  count?: number;
}

interface Props<T extends string> {
  /** Nome do filtro, lido por leitor de tela e mostrado antes do valor. */
  label: string;
  value: T;
  options: FilterOption<T>[];
  onChange: (value: T) => void;
  className?: string;
}

/**
 * Filtro num botão só: mostra a opção escolhida e abre a lista ao clicar.
 * Teclado: setas navegam, Enter escolhe, Esc fecha.
 */
export function FilterMenu<T extends string>({ label, value, options, onChange, className }: Props<T>) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();
  const current = options.find((o) => o.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const choose = (v: T) => {
    onChange(v);
    setOpen(false);
  };

  const openAt = () => {
    setActive(
      Math.max(
        0,
        options.findIndex((o) => o.value === value),
      ),
    );
    setOpen(true);
  };

  return (
    <div ref={root} className={cn('relative', className)}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => (open ? setOpen(false) : openAt())}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            openAt();
            return;
          }
          if (!open) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, options.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            choose(options[active]!.value);
          }
        }}
        className="flex h-10 items-center gap-2 rounded-lg border border-line-strong bg-surface px-3.5 text-sm transition-colors hover:bg-surface-2 focus:ring-2 focus:ring-teal-300 focus:outline-none"
      >
        <span className="text-fg-subtle">{label}:</span>
        <span className="font-medium text-fg">{current?.label}</span>
        <CaretDown
          aria-hidden
          className={cn('size-3.5 text-gray-400 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="absolute left-0 z-30 mt-2 min-w-56 overflow-hidden rounded-xl border border-line-strong bg-surface py-1"
        >
          {options.map((o, i) => {
            const selected = o.value === value;
            return (
              <li
                key={o.value}
                role="option"
                aria-selected={selected}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(o.value)}
                className={cn(
                  'flex cursor-pointer items-center gap-3 px-3.5 py-2.5 text-sm',
                  i === active && 'bg-surface-2',
                  selected ? 'font-medium text-nav-active-fg' : 'text-fg-muted',
                )}
              >
                <span className="flex-1">{o.label}</span>
                {o.count !== undefined && (
                  <span className="text-xs text-fg-subtle tabular-nums">{o.count}</span>
                )}
                <Check aria-hidden className={cn('size-4', selected ? 'opacity-100' : 'opacity-0')} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
